/* eslint-disable no-undef */
/**
 * Safe storage utility that handles localStorage with environment checks
 * Prevents "localStorage is not defined" errors in SSR or testing environments
 */

// Check if localStorage is available
const isLocalStorageAvailable = (): boolean => {
  try {
    return globalThis?.localStorage != null;
  } catch {
    return false;
  }
};

// Safe localStorage wrapper
export const safeStorage = {
  getItem: (key: string): string | null => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available, returning null for key:', key);
      return null;
    }

    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },

  setItem: (key: string, value: string): boolean => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot set key:', key);
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error setting localStorage:', error);
      return false;
    }
  },

  removeItem: (key: string): boolean => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot remove key:', key);
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  },

  clear: (): boolean => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot clear');
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  },
};

// Token-specific utilities for convenience
export const tokenStorage = {
  getAccessToken: (): string | null => safeStorage.getItem('accessToken'),
  setAccessToken: (token: string): boolean => safeStorage.setItem('accessToken', token),
  removeAccessToken: (): boolean => safeStorage.removeItem('accessToken'),

  getRefreshToken: (): string | null => safeStorage.getItem('refreshToken'),
  setRefreshToken: (token: string): boolean => safeStorage.setItem('refreshToken', token),
  removeRefreshToken: (): boolean => safeStorage.removeItem('refreshToken'),

  clearTokens: (): boolean => {
    const accessRemoved = safeStorage.removeItem('accessToken');
    const refreshRemoved = safeStorage.removeItem('refreshToken');
    return accessRemoved && refreshRemoved;
  },
};
